<template lang="pug">
    .floor-plan-table(
        :class="tableClasses"
        :style="tableStyle"
        @mousedown.prevent="onMouseDown"
        @click.stop="onClick"
        @dragover.prevent="onDragOver"
        @dragenter.prevent="onDragEnter"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
    )
        .floor-plan-table__combo-badge(v-if="inCombination && editMode" :title="$t('Part of combination')")
            i.el-icon-fa-link.fas
        .floor-plan-table__chairs(aria-hidden="true")
            template(v-if="table.shape === 'round'")
                .floor-plan-table__chair(
                    v-for="n in table.seats"
                    :key="n"
                    :style="getRoundChairStyle(n, table.seats)"
                )
            template(v-else)
                .floor-plan-table__chair(
                    v-for="(chair, idx) in squareChairs"
                    :key="idx"
                    :style="chair.style"
                )
        .floor-plan-table__label {{ table.number }}
        .floor-plan-table__seats {{ table.seats }}
            i.el-icon-fa-user.fas
</template>

<script>
export default {
    name: 'FloorPlanTable',
    props: {
        table: {
            type: Object,
            required: true
        },
        selected: {
            type: Boolean,
            default: false
        },
        multiSelected: {
            type: Boolean,
            default: false
        },
        inCombination: {
            type: Boolean,
            default: false
        },
        editMode: {
            type: Boolean,
            default: false
        },
        selectionModeActive: {
            type: Boolean,
            default: false
        },
        dropActive: {
            type: Boolean,
            default: false
        },
        reservationInfo: {
            type: Object,
            default: null
        },
        dynamicStatus: {
            type: String,
            default: null
        }
    },
    data: function () {
        return {
            dragHover: false
        };
    },
    computed: {
        squareChairs: function () {
            const sides = this.table.seatSides || { top: 2, bottom: 2, left: 0, right: 0 };
            const w = this.table.width;
            const h = this.table.height;
            const chairs = [];
            const sideConfigs = [
                {
                    key: 'top',
                    style: (i, count) => { const sp = w / (count + 1); return { left: (sp * (i + 1) - 5) + 'px', top: '-14px' }; }
                },
                {
                    key: 'bottom',
                    style: (i, count) => { const sp = w / (count + 1); return { left: (sp * (i + 1) - 5) + 'px', top: (h + 4) + 'px' }; }
                },
                {
                    key: 'left',
                    style: (i, count) => { const sp = h / (count + 1); return { left: '-14px', top: (sp * (i + 1) - 4) + 'px', width: '8px', height: '10px', borderRadius: '2px 0 0 2px' }; }
                },
                {
                    key: 'right',
                    style: (i, count) => { const sp = h / (count + 1); return { left: (w + 4) + 'px', top: (sp * (i + 1) - 4) + 'px', width: '8px', height: '10px', borderRadius: '0 2px 2px 0' }; }
                }
            ];
            sideConfigs.forEach((sc) => {
                const count = sides[sc.key] || 0;
                for (let i = 0; i < count; i++) {
                    chairs.push({ style: sc.style(i, count) });
                }
            });
            return chairs;
        },
        tableClasses: function () {
            const classes = {};
            classes['floor-plan-table--' + this.table.shape] = true;
            const effectiveStatus = this.table.blocked ? 'blocked' : (this.dynamicStatus || 'available');
            classes['floor-plan-table--' + effectiveStatus] = true;
            classes['floor-plan-table--selected'] = this.selected;
            classes['floor-plan-table--multi-selected'] = this.multiSelected;
            classes['floor-plan-table--in-combination'] = this.inCombination && this.editMode;
            classes['floor-plan-table--blocked'] = this.table.blocked;
            classes['floor-plan-table--draggable'] = this.editMode;
            classes['floor-plan-table--drop-active'] = this.dropActive && !this.table.blocked;
            classes['floor-plan-table--drop-hover'] = this.dragHover && !this.table.blocked;
            classes['floor-plan-table--has-reservation'] = !!this.reservationInfo;
            return classes;
        },
        tableStyle: function () {
            return {
                left: this.table.x + 'px',
                top: this.table.y + 'px',
                width: this.table.width + 'px',
                height: this.table.height + 'px',
                transform: this.table.rotation ? 'rotate(' + this.table.rotation + 'deg)' : 'none'
            };
        }
    },
    watch: {
        dropActive: function (val) {
            if (!val) {
                this.dragHover = false;
            }
        }
    },
    methods: {
        getRoundChairStyle: function (n, total) {
            const angle = ((n - 1) / total) * 2 * Math.PI - Math.PI / 2;
            const radius = Math.max(this.table.width, this.table.height) / 2 + 12;
            const cx = this.table.width / 2;
            const cy = this.table.height / 2;
            return {
                left: (cx + radius * Math.cos(angle) - 5) + 'px',
                top: (cy + radius * Math.sin(angle) - 4) + 'px'
            };
        },
        onClick: function (event) {
            this.$emit('table-click', {
                id: this.table.id,
                additive: this.selectionModeActive || !!(event && (event.ctrlKey || event.metaKey || event.shiftKey))
            });
        },
        onMouseDown: function (event) {
            if (this.editMode && !this.selectionModeActive) {
                this.$emit('drag-start', {
                    table: this.table,
                    event: event,
                    additive: !!(event && (event.ctrlKey || event.metaKey || event.shiftKey))
                });
            }
        },
        onDragOver: function (e) {
            if (!this.dropActive || this.table.blocked) return;
            e.dataTransfer.dropEffect = 'move';
        },
        onDragEnter: function () {
            if (!this.dropActive || this.table.blocked) return;
            this.dragHover = true;
        },
        onDragLeave: function (e) {
            // Only leave if actually leaving the element (not entering a child)
            if (!this.$el.contains(e.relatedTarget)) {
                this.dragHover = false;
            }
        },
        onDrop: function (e) {
            this.dragHover = false;
            if (!this.dropActive || this.table.blocked) return;
            const reservationId = e.dataTransfer.getData('application/x-reservation-id')
                || e.dataTransfer.getData('text/plain');
            if (reservationId) {
                this.$emit('reservation-drop', {
                    tableId: this.table.id,
                    reservationId: reservationId
                });
            }
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.floor-plan-table
    position: absolute
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    border: 2px solid #c0c4cc
    background: #f5f7fa
    cursor: pointer
    user-select: none
    transition: box-shadow 0.2s, border-color 0.2s
    z-index: 1

    &:hover
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15)
        z-index: 2

    &--round
        border-radius: 50%

    &--square
        border-radius: 6px

    // Status colors
    &--available
        background: #f0f9eb
        border-color: #67c23a

    &--reserved
        background: #fdf6ec
        border-color: #e6a23c

    &--occupied
        background: #fef0f0
        border-color: #f56c6c

    &--blocked
        background: #e9e9eb
        border-color: #909399
        opacity: 0.7

    &--selected
        border-color: $--color-primary !important
        box-shadow: 0 0 0 3px rgba($--color-primary, 0.3) !important
        z-index: 3

    &--multi-selected
        border-color: #409eff !important
        border-width: 3px !important
        box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.35) !important
        z-index: 4

    &--in-combination
        outline: 1px dashed #909399
        outline-offset: 3px

    &__combo-badge
        position: absolute
        top: -8px
        right: -8px
        width: 16px
        height: 16px
        border-radius: 50%
        background: #909399
        color: #fff
        font-size: 9px
        display: flex
        align-items: center
        justify-content: center
        z-index: 6
        pointer-events: none

    &--draggable
        cursor: move

    &--drop-active
        border-style: dashed
        transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s

    &--drop-hover
        border-color: $--color-primary !important
        border-style: solid
        box-shadow: 0 0 0 4px rgba($--color-primary, 0.35), 0 4px 16px rgba($--color-primary, 0.2) !important
        transform: scale(1.08)
        z-index: 5

    &__chairs
        position: absolute
        top: 0
        left: 0
        width: 100%
        height: 100%
        pointer-events: none
        overflow: visible

    &__chair
        position: absolute
        width: 10px
        height: 8px
        background: #909399
        border-radius: 2px 2px 0 0
        opacity: 0.65


    &__label
        font-weight: 700
        font-size: 1rem
        color: #303133
        line-height: 1

    &__seats
        font-size: 0.7rem
        color: #606266
        margin-top: 2px
        i
            font-size: 0.6rem
            margin-left: 2px

</style>
